import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DatesProvider } from "../../../../../dates/DatesContext";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import BreadcrumbDates from "../../../../../components/core/blocks/BreadcrumbDates";
import { Col, Row } from "react-bootstrap";
import { DASHBOARD_LINKS, DSP_DASHBOARD_BREADCRUMB_SPEC } from "../../../../../utils/dashboardLinks";
import { useDates } from "../../../../../dates/DatesContext";
import { useDateFormatter } from "../../../../../i18n/useDateFormatter";
import { useModuleEnabled } from "../../../../../redux/selectors/modules";
import MetricsCard from "../../../../../components/custom/merchandise/MetricsCard";
import getMetricSpec from "../../../../../modules/merchandising/utils/metrics";
import { useDebounce } from "@reactuses/core";
import { useAppSelector } from "../../../../../redux/hooks";
import useDSPCreativeData from "../../../../../modules/dsp/hooks/useDSPCreativeData";
import { DSPCreativesTable } from "../../../../../modules/dsp/components/DSPCreativesTable";
import DSPOverlapDashboard from "../../../../../modules/dsp/components/DSPOverlapDashboard";
import DSPOrderForm from "../../../../../modules/dsp/forms/DSPOrderForm";
import { Button } from "../../../../../components/core/basic/Button";
import { useQueryClient } from "@tanstack/react-query";
import DSPGeoDashboard from "../../../../../modules/dsp/components/DSPGeoDashboard";
import { Tabs, TabList, Tab, TabPanel } from "../../../../../components/core/basic/Tabs";
import useQueryParams from "../../../../../hooks/useQueryParams";
import { css } from "../../../../../../styled-system/css";
import useDSPSellerInfo from "../../../../../modules/dsp/hooks/useDSPSellerInfo";
import { Modal } from "../../../../../components/core/basic/Modal";
import { useSimpleCan } from "../../../../../config/casl/casl";
import { useDSPCampaignsDatasetsQuery } from "../../../../../modules/dsp/hooks/useDSPCampaignDatasetsData";
import { usePersistedTableState } from "../../../../../components/table/usePersistedTableState";
import DSPOrdersTable from "../../../../../modules/dsp/components/DSPOrdersTable";
import { idsToVisibilityState } from "../../../../../components/table/utils";
import { METRIC_COLUMNS_BY_ENTITY } from "../../../../../modules/merchandising/components/tables/metricsByEntity";
import { adaptDatasetsFilters } from "../../../../../utils/datasetsFilters";

const ALL_DSP_ORDER_COLUMNS = [
  "order",
  "delivery_status",
  "state",
  "start_date",
  "end_date",
  "goal",
  ...METRIC_COLUMNS_BY_ENTITY.dspCampaign,
  "creation_date_time",
];

const DEFAULT_VISIBLE_COLUMNS = [
  "order",
  "state",
  "goal",
  "dsp_sales",
  "dsp_total_sales",
  "dsp_ecpm",
  "dsp_impressions",
  "dsp_clicks",
  "dsp_cost",
  "dsp_total_purchases",
  "dsp_units",
  "dsp_ctr",
  "creation_date_time",
];

const defaultColumnVisibility = idsToVisibilityState(DEFAULT_VISIBLE_COLUMNS, ALL_DSP_ORDER_COLUMNS);

const Dashboard = () => {
  const { t } = useTranslation(["dsp"]);
  const [showOrderCreationModal, setShowOrderCreationModal] = useState(false);
  const { dspAdvertiser, isLoading: isSellerInfoLoading } = useDSPSellerInfo();

  const [tab, setTab] = useQueryParams("tab", "orders");

  const channel = useAppSelector((state) => state.channel);
  const hasDSPModule = useModuleEnabled("DSP");
  const userCanCreateDSPEntity = useSimpleCan("create", "DSPEntity");

  const queryClient = useQueryClient();

  const { state, setters, initialState } = usePersistedTableState({
    persistKey: "dspOrdersTable",
    initialState: {
      sorting: [{ id: "dsp_sales", desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 },
      columnPinning: {
        start: ["order"],
        end: [],
      },
      columnVisibility: defaultColumnVisibility,
    },
  });

  const debouncedSearch = useDebounce(state.globalFilter, 500);

  const overviewFilters = useMemo(() => {
    const columnFilters = adaptDatasetsFilters(state.columnFilters);
    const searchFilter = debouncedSearch ? [{ id: "name", value: { op: "contains", value: debouncedSearch } }] : [];
    return [...columnFilters, ...searchFilter];
  }, [state.columnFilters, debouncedSearch]);

  const { group, start, end } = useDates();
  const longDate = useDateFormatter("long");

  const dspOrgTotalsQuery = useDSPCampaignsDatasetsQuery("org_id", false, overviewFilters);
  const dspOrgTimelineQuery = useDSPCampaignsDatasetsQuery("org_id,start_date", false, overviewFilters);

  const {
    data: dspCreativeStatsData,
    metadata: dspCreativeMetadata,
    isLoading: isDSPCreativeDataLoading,
  } = useDSPCreativeData("creative_id", true, true);

  const isLoading = isSellerInfoLoading || dspOrgTotalsQuery.isLoading || dspOrgTimelineQuery.isLoading;

  const timelineData = useMemo(
    () => (dspOrgTimelineQuery.data?.data ?? []).map((d) => ({ ...d, name: d.start_date })),
    [dspOrgTimelineQuery.data?.data],
  );

  const dspCreativesData = useMemo(() => {
    const combinedData = dspCreativeStatsData?.map((stats) => {
      const metadata = dspCreativeMetadata?.find((row) => row.creative_id === stats.creative_id) ?? {};
      return { ...metadata, ...stats };
    });
    return combinedData;
  }, [dspCreativeMetadata, dspCreativeStatsData]);

  const breadCrumbItems = [
    {
      name: t(($) => $.dsp["DSP Dashboard"]),
      href: `/user/dsp/${dspAdvertiser?.id}/dashboard`,
      unsorted: true,
      dropdownLinkOptions: DASHBOARD_LINKS,
      dropdownSubtitle: t(($) => $.dsp.Dashboards),
    },
  ];

  // Overview stat tile and chart metrics
  const overviewMetrics = useMemo(() => {
    return [
      getMetricSpec("dsp_sales"),
      getMetricSpec("dsp_total_sales"),
      getMetricSpec("dsp_ecpm"),
      getMetricSpec("dsp_impressions"),
      getMetricSpec("dsp_clicks"),
      getMetricSpec("dsp_cost"),
      getMetricSpec("dsp_total_purchases"),
      getMetricSpec("dsp_units"),
      getMetricSpec("dsp_total_units"),
      getMetricSpec("dsp_ctr"),
      getMetricSpec("dsp_total_acos"),
      getMetricSpec("dsp_total_roas"),
      getMetricSpec("dsp_total_detail_page_views"),
      getMetricSpec("dsp_total_detail_page_view_rate"),
      getMetricSpec("dsp_total_add_to_cart"),
      getMetricSpec("dsp_new_to_brand_detail_page_views"),
      getMetricSpec("dsp_new_to_brand_detail_page_views_rate"),
      getMetricSpec("dsp_total_new_to_brand_dpvs"),
      getMetricSpec("dsp_total_new_to_brand_dpv_rate"),
      getMetricSpec("dsp_brand_searches"),
      getMetricSpec("dsp_brand_searches_rate"),
    ];
  }, []);

  if (
    hasDSPModule &&
    !breadCrumbItems[0].dropdownLinkOptions?.find((o) => o.name === DSP_DASHBOARD_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0].dropdownLinkOptions.push(DSP_DASHBOARD_BREADCRUMB_SPEC);
  }

  if (!hasDSPModule || channel.currentChannel !== "amazon") {
    return <Navigate to="/user/dashboard" replace />;
  }

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <Row className="pb-5">
        <Col xs={12} className="px-5">
          <MetricsCard
            id="dspOverviewDashboard"
            title={t(($) => $.dsp["DSP Dashboard"])}
            initialSelectedMetric={["dsp_sales", "dsp_impressions", "dsp_ecpm", "dsp_total_purchases", "dsp_clicks"]}
            totals={{
              data: dspOrgTotalsQuery.data?.data?.[0] ?? {},
              loading: isLoading,
            }}
            timeline={{
              data: timelineData,
              loading: isLoading,
            }}
            metrics={overviewMetrics}
            group={group}
            showMetricsSelector={true}
            context={"dspCampaign"}
            byline={
              <>
                {longDate.format(start.toDate())}
                {" - "}
                {longDate.format(end.toDate())}
              </>
            }
          >
            <Col xs={12}>
              <Tabs selectedKey={tab} onSelectionChange={setTab}>
                <TabList>
                  <Tab id="orders">{t(($) => $.dsp["DSP Orders"])}</Tab>
                  <Tab id="creatives">{t(($) => $.dsp["DSP Creatives"])}</Tab>
                  <Tab id="overlap">{t(($) => $.dsp["Audience Overlap"])}</Tab>
                  <Tab id="geography">{t(($) => $.dsp.Geography)}</Tab>
                </TabList>
                <TabPanel id="orders">
                  <Col xs={12} className="d-flex justify-content-end align-items center mx-auto">
                    <Modal
                      isOpen={showOrderCreationModal}
                      onOpenChange={(isOpen) => {
                        setShowOrderCreationModal(isOpen);
                      }}
                      css={{
                        width: "70vw",
                        maxHeight: "95vh",
                        paddingX: "xlarge",
                      }}
                    >
                      <Row className={css({ marginTop: "large" })}>
                        <Col xs={12}>
                          <DSPOrderForm
                            initialFormData={{}}
                            createMode={true}
                            onCancel={() => {
                              setShowOrderCreationModal(false);
                            }}
                            onSuccessfulSubmit={() => {
                              setShowOrderCreationModal(false);
                              queryClient.invalidateQueries({
                                predicate: (query) =>
                                  query.queryKey[0] === "dspDataSets" ||
                                  query.queryKey[0]?.entity === "dspOrdersPaginated",
                              });
                            }}
                            dspAdvertiserId={dspAdvertiser?.advertiser_id}
                          />
                        </Col>
                      </Row>
                    </Modal>
                    {userCanCreateDSPEntity && (
                      <Button
                        onPress={() => {
                          setShowOrderCreationModal(true);
                        }}
                      >
                        {t(($) => $.dsp["Create a New Order"])}
                      </Button>
                    )}
                  </Col>
                  <Col xs={12}>
                    <DSPOrdersTable
                      dspAdvertiser={dspAdvertiser}
                      state={state}
                      setters={setters}
                      initialState={initialState}
                    />
                  </Col>
                </TabPanel>
                <TabPanel id="creatives">
                  <Col xs={12}>
                    <DSPCreativesTable data={dspCreativesData} isLoading={isDSPCreativeDataLoading} />
                  </Col>
                </TabPanel>
                <TabPanel id="geography">
                  <Col xs={12}>
                    <DSPGeoDashboard showAll />
                  </Col>
                </TabPanel>
                <TabPanel id="overlap">
                  <Col xs={12}>
                    <DSPOverlapDashboard />
                  </Col>
                </TabPanel>
              </Tabs>
            </Col>
          </MetricsCard>
        </Col>
      </Row>
    </div>
  );
};

const DSPDashboard = () => {
  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/dsp/$dspAdvertiser/dashboard")({
  component: DSPDashboard,
  staticData: { application: "Advertising" },
});
